/**
 * TradesPage - Dedicated page for managing P2P NFT trades
 *
 * Two tabs: Pending | History
 * Pending tab shows all active trades with "your turn" sorted first.
 * Sidebar badge shows pending incoming count.
 *
 * Entry point: /trades (new sidebar item after Inbox)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FeatherArrowLeftRight, FeatherPlus, FeatherInbox } from '@subframe/core';
import { useAuthStore } from '../store/authStore';
import {
  getTrades,
  getTradeDetails,
  acceptTrade,
  declineTrade,
  cancelTrade,
  startSigning,
} from '../api/trades';
import type { NFTTrade, TradeListResponse, SigningResponse } from '../api/trades';
import TradeCard from '../components/trades/TradeCard';
import TradeBuilder from '../components/trades/TradeBuilder';
import SigningWizard from '../components/trades/SigningWizard';
import { toSigningPayload } from '../lib/wallet';
import { Tabs } from '../ui/components/Tabs';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';

type TabType = 'pending' | 'history';

export default function TradesPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const myAddress = user?.xrpl_address || '';

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Tab state
  const initTab = (() => {
    const tab = searchParams.get('tab') as TabType;
    return ['pending', 'history'].includes(tab) ? tab : 'pending';
  })();
  const [activeTab, setActiveTab] = useState<TabType>(initTab);

  // Trade data
  const [trades, setTrades] = useState<NFTTrade[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals
  const [showBuilder, setShowBuilder] = useState(false);
  const [signingState, setSigningState] = useState<{
    tradeId: string;
    steps: any[];
  } | null>(null);

  // Counter mode
  const [counterTrade, setCounterTrade] = useState<NFTTrade | null>(null);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const loadTrades = useCallback(async () => {
    if (!isAuthenticated || !myAddress) return;
    setIsLoading(true);
    setError(null);

    try {
      const result = await getTrades(activeTab, { limit: 50 });
      setTrades(result.trades);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load trades');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, isAuthenticated, myAddress]);

  useEffect(() => {
    loadTrades();
  }, [loadTrades]);

  const handleAccept = async (tradeId: string) => {
    if (!confirm('Are you sure you want to accept this trade? You will need to sign transactions to complete it.')) return;
    try {
      await acceptTrade(tradeId);
      loadTrades();
    } catch (err: any) {
      alert(err.message || 'Failed to accept trade');
    }
  };

  const handleDecline = async (tradeId: string) => {
    if (!confirm('Are you sure you want to decline this trade?')) return;
    try {
      await declineTrade(tradeId);
      loadTrades();
    } catch (err: any) {
      alert(err.message || 'Failed to decline trade');
    }
  };

  const handleCancel = async (tradeId: string) => {
    if (!confirm('Are you sure you want to cancel this trade?')) return;
    try {
      await cancelTrade(tradeId);
      loadTrades();
    } catch (err: any) {
      alert(err.message || 'Failed to cancel trade');
    }
  };

  const handleCounter = async (tradeId: string) => {
    try {
      const result = await getTradeDetails(tradeId);
      setCounterTrade(result.trade);
    } catch (err: any) {
      alert(err.message || 'Failed to load trade for counter');
    }
  };

  const handleTradeClick = async (tradeId: string) => {
    // Find the trade
    const trade = trades.find((t) => t.id === tradeId);
    if (!trade) return;

    // Either party can sign when accepted or signing
    if (trade.status === 'accepted' || trade.status === 'signing') {
      try {
        const result = await startSigning(tradeId);
        if (result.alreadySigned) {
          // User's side is already fully signed — refresh to show updated state
          loadTrades();
          return;
        }
        startSigningFlow(tradeId, trade, result);
      } catch (err: any) {
        alert(err.message || 'Failed to start signing');
      }
      return;
    }
  };

  const startSigningFlow = (tradeId: string, trade: NFTTrade, signingData: SigningResponse) => {
    const steps: any[] = [];

    // Add NFT offer steps. Normalize each to a wallet-agnostic SigningPayload so the
    // wizard works for both Xaman (QR) and client-signing (joey-wc) sessions.
    if (signingData.payloads) {
      for (const p of signingData.payloads) {
        steps.push({
          type: 'nft_offer',
          label: `Offer NFT ${p.nftTokenId.slice(0, 8)}...`,
          nftTokenId: p.nftTokenId,
          payload: toSigningPayload(p.signingIntent ?? p.payload),
          status: 'pending',
        });
      }
    }

    // Add escrow step
    if (signingData.escrowPayload || signingData.escrowSigningIntent) {
      const isInitiator = trade.initiator_address === myAddress;
      const xrpDrops = isInitiator ? trade.initiator_xrp_drops : trade.recipient_xrp_drops;
      const xrpAmount = Number(xrpDrops) / 1_000_000;
      steps.push({
        type: 'escrow',
        label: `Escrow ${xrpAmount.toLocaleString()} XRP`,
        payload: toSigningPayload(signingData.escrowSigningIntent ?? signingData.escrowPayload),
        status: 'pending',
      });
    }

    setSigningState({ tradeId, steps });
  };

  const handleTradeCreated = () => {
    // Stay on pending tab - new trade will appear there
    setActiveTab('pending');
    setSearchParams({ tab: 'pending' });
    loadTrades();
  };

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6">
        {/* Page header */}
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex flex-col items-start gap-2">
            <span className="text-heading-1 font-heading-1 text-default-font">
              Trades
            </span>
            <span className="text-body font-body text-subtext-color">
              Propose, negotiate, and execute peer-to-peer NFT trades
            </span>
          </div>
          <button
            onClick={() => setShowBuilder(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-primary text-white text-body-bold font-body-bold hover:bg-brand-primary/90 transition-colors shadow-sm"
          >
            <FeatherPlus className="h-4 w-4" />
            <span className="hidden sm:inline">New Trade</span>
          </button>
        </div>

        {/* Tabs */}
        <Tabs>
          <Tabs.Item
            active={activeTab === 'pending'}
            onClick={() => handleTabChange('pending')}
          >
            Pending
          </Tabs.Item>
          <Tabs.Item
            active={activeTab === 'history'}
            onClick={() => handleTabChange('history')}
          >
            History
          </Tabs.Item>
        </Tabs>

        {/* Loading state */}
        {isLoading && (
          <div className="flex w-full items-center justify-center py-12" role="status">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
            <span className="ml-3 text-body font-body text-subtext-color">Loading trades...</span>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="w-full bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <svg
                className="h-5 w-5 text-red-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Failed to load trades
                </h3>
                <p className="mt-2 text-sm text-red-700">{error}</p>
                <button
                  onClick={() => loadTrades()}
                  className="mt-3 text-sm font-medium text-red-800 hover:text-red-900"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && trades.length === 0 && (
          <div className="flex w-full flex-col items-center justify-center py-12 rounded-md border border-solid border-neutral-border bg-default-background">
            <FeatherInbox className="h-12 w-12 text-subtext-color" />
            <span className="mt-4 text-body-bold font-body-bold text-default-font">
              {activeTab === 'pending'
                ? 'No pending trades'
                : 'No trade history'}
            </span>
            <span className="mt-1 text-body font-body text-subtext-color text-center max-w-md">
              {activeTab === 'pending'
                ? "Active trade proposals will appear here"
                : "Completed, declined, and expired trades appear here"}
            </span>
            {activeTab !== 'history' && (
              <button
                onClick={() => setShowBuilder(true)}
                className="mt-4 text-body font-body text-brand-primary hover:underline"
              >
                Create your first trade
              </button>
            )}
          </div>
        )}

        {/* Trades list */}
        {!isLoading && !error && trades.length > 0 && (
          <div className="w-full space-y-3">
            {trades.map((trade) => (
              <TradeCard
                key={trade.id}
                trade={trade}
                currentUserAddress={myAddress}
                onAccept={handleAccept}
                onDecline={handleDecline}
                onCancel={handleCancel}
                onCounter={handleCounter}
                onClick={handleTradeClick}
              />
            ))}
          </div>
        )}

        {/* Load more */}
        {total > trades.length && (
          <div className="w-full text-center">
            <button
              onClick={() => {
                // Could implement pagination here
              }}
              className="text-body font-body text-brand-primary hover:underline"
            >
              Show more ({total - trades.length} remaining)
            </button>
          </div>
        )}
      </div>

      {/* Trade Builder Modal */}
      {showBuilder && (
        <TradeBuilder
          isOpen={showBuilder}
          onClose={() => setShowBuilder(false)}
          onTradeCreated={handleTradeCreated}
        />
      )}

      {/* Counter Trade Modal */}
      {counterTrade && (() => {
        // Determine if current user is the initiator
        const isInitiator = counterTrade.initiator_address === myAddress;

        // Map items from "my perspective" for the UI:
        // - "YOU GIVE" should show what the current user gives
        // - "YOU WANT" should show what the current user wants (what the other party gives)
        const myItems = isInitiator ? counterTrade.initiator_items : counterTrade.recipient_items;
        const theirItems = isInitiator ? counterTrade.recipient_items : counterTrade.initiator_items;
        const myXrp = isInitiator
          ? Number(counterTrade.initiator_xrp_drops) / 1_000_000
          : Number(counterTrade.recipient_xrp_drops) / 1_000_000;
        const theirXrp = isInitiator
          ? Number(counterTrade.recipient_xrp_drops) / 1_000_000
          : Number(counterTrade.initiator_xrp_drops) / 1_000_000;

        const mapItem = (i: typeof counterTrade.initiator_items[0]) => ({
          nft_token_id: i.nft_token_id,
          name: i.nft_name,
          image_url: i.nft_image_url,
          thumbnail_url: i.nft_thumbnail_url,
          rarity_rank: i.rarity_rank,
          collection_name: i.collection_name,
          collection_id: i.collection_id,
        });

        return (
          <TradeBuilder
            isOpen={true}
            onClose={() => setCounterTrade(null)}
            onTradeCreated={handleTradeCreated}
            recipientAddress={isInitiator ? counterTrade.recipient_address : counterTrade.initiator_address}
            counterTradeId={counterTrade.id}
            counterInitiatorNfts={myItems.map(mapItem)}
            counterRecipientNfts={theirItems.map(mapItem)}
            counterInitiatorXrp={myXrp}
            counterRecipientXrp={theirXrp}
            isUserInitiator={isInitiator}
          />
        );
      })()}

      {/* Signing Wizard */}
      {signingState && (
        <SigningWizard
          tradeId={signingState.tradeId}
          steps={signingState.steps}
          onComplete={() => {
            setSigningState(null);
            loadTrades();
          }}
          onClose={() => setSigningState(null)}
        />
      )}
    </DefaultPageLayout>
  );
}
